<template>
  <div>
    <table class="dialog-table">
      <thead>
        <tr>
          <th></th>
          <th v-if="showSums">%</th>
          <th v-if="showSums">{{ $t('amount_lower') }}</th>
          <th v-if="showSums">{{ $t('debit_lower') }}</th>
          <th v-if="!hideRemoveBtn"></th>
        </tr>
      </thead>
      <tbody>
        <tbody style="display: contents" v-for="(row, idx) in theModel" :key="'conn_line_'+idx">
          <tr>
            <td>
              <div class="row no-wrap items-center">
                <span style="font-weight: 500; align-self: flex-start;">{{ idx + 1 }}.&nbsp;</span>
                <bill-row v-if="row.bill && !(row.nonExistent == true && showNonExistent)" :el="row.bill" :bill-type="row.billRowType" />
                <movement-row v-else-if="row.movement && (row.nonExistent == true && showNonExistent || !row.nonExistent)" :el="row.movement" :show-positions="false" />
                <project-row v-else-if="row.project" :el="row.project" />
                <span v-if="showLockStatus && rowLockStatus(row)" class="q-ml-xs inline-flex self-start">
                  <q-tooltip :delay="300" anchor="top middle" self="center middle">
                    {{ rowLockStatus(row) === 'locked_by_other' ? $t('entity_locked_other_user') : $t('entity_locked_or_other_reason') }}
                  </q-tooltip>
                  <app-icon name="lock" width="18px" class="lock-icon" />
                </span>
              </div>
            </td>
            <td v-if="showSums" class="shrink-col">
              <native-input
                :ref="'percent_input_'+idx"
                v-model="row.percent"
                input-type="number"
                :edit-on-click="true"
                :min-number-value="0"
                :max-number-value="(row.maxPercent ? row.maxPercent : this.maxPercent) || undefined"
                :disabled-field="!row.canEditAmounts"
                @blur="recalcRow(row, 'percent')"
              />
            </td>
            <td v-if="showSums" class="shrink-col">
              <native-input
                :ref="'amount_input_'+idx"
                v-model="row.amount"
                input-type="number"
                :edit-on-click="true"
                :min-number-value="0"
                :max-number-value="(row.limitAmount ? row.limitAmount : (row.maxAmount ? row.maxAmount : this.maxAmount)) || undefined"
                :disabled-field="!row.canEditAmounts"
                @blur="recalcRow(row, 'amount')"
              />
            </td>
            <td v-if="showSums" class="shrink-col">
              <native-input
                :ref="'debit_input_'+idx"
                v-model="row.debit"
                input-type="number"
                :edit-on-click="true"
                :min-number-value="0"
                :max-number-value="row.maxDebit || undefined"
                :disabled-field="!row.canEditDebits"
                @blur="recalcRow(row, 'debit')"
              />
            </td>
            <!-- income connection is not removable from the bill info dialog -->
            <td class="cell-actions" v-if="!hideRemoveBtn">
              <span
                v-if="!(row.nonExistent == true && showNonExistent) && (row.canBeDeleted || !this.maxAmount)"
                class="table-remove"
                :title="$t('remove')"
                @click="cur_idx = idx"
              >
                <app-icon
                  name="close"
                  width="16px"
                  color="#979797"
                  hover-color="#616161"
                  cursor="pointer"
                />
                <q-menu>
                  <q-card>
                    <q-card-section class="row items-center">
                      <app-icon
                        name="delete-outline"
                        width="18px"
                        color="#979797"
                      />
                      <span class="q-ml-sm">
                        {{ $t("sure_delete") }}
                      </span>
                    </q-card-section>

                    <q-card-actions @keydown="windowKeyDown" align="right">
                      <q-btn
                        size="sm"
                        flat
                        :label="$t('cancel')"
                        color="dark"
                        :ref="'cancelbtn'+idx"
                        v-close-popup
                      />
                      <q-btn
                        size="sm"
                        flat
                        :label="$t('remove')"
                        autofocus
                        color="dark"
                        :ref="'okbtn'+idx"
                        @click="remove(idx)"
                        v-close-popup
                      />
                    </q-card-actions>
                  </q-card>
                </q-menu>
              </span>
            </td>
          </tr>
          <!-- position rows -->
          <tbody style="display: contents" v-if="row.movement">
            <tr v-for="(storagepos, idsx) in row.movement.Items" :key="'stp'+idsx">
              <td class="pos_cell">
                <div class="row no-wrap items-baseline q-ml-md">
                  {{ idsx + 1 }})&nbsp;
                  <position-row
                    :id="storagepos.Id"
                    :el="getPositionFromNameInstance(storagepos)"
                    :hide-info-btn="true"
                  />
                </div>
              </td>
              <td v-if="showSums" class="shrink-col pos_cell">
                <span style="padding-left: 6px;">
                  {{ storagepos.ItemQuantity }}
                </span>
              </td>
              <td v-if="showSums" class="shrink-col pos_cell">
                <span style="padding-left: 6px;">
                  {{ storagepos.TotalAmount }}
                </span>
              </td>
              <td v-if="showSums" class="shrink-col pos_cell">
              </td>
              <!-- income connection is not removable from the bill info dialog -->
              <td v-if="!hideRemoveBtn" class="pos_cell">
              </td>
            </tr>
          </tbody>
        </tbody>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import _ from "lodash";
import { Component, Emit, Prop, Vue, Watch } from "vue-facing-decorator";
import { V2ParameterValueType } from "../../../models/etc/parameter-types";
import { BillLinkedTypeDescriptor, BillSearchedTypeDescriptor, ParameterInstanceContainerType, V2FieldValueDisplayType, V2RecordAxisType } from "../../../models/etc/types";
import { mapStorageItemInstanceToParameterInstance } from "../../../models/helpers/parameter-helpers";
import { StorageItemInMovement } from "../../../models/interfaces/finances/finance-models";
import { ParameterInstance, ParameterInstanceContainer } from "../../../models/interfaces/parameters/parameter-models";
import { ConnectionLine, BillRowProps, useEntityStore } from "../../../store/heartbeat-workers/useEntityStore";
import { LockedEntityType } from "../../../store/interfaces/types.analytics";
import { roundTo } from "../../../utils/auxfunc";
import NativeInput from "../../common/basic/NativeInput.vue";
import PositionRow from "./storageshared/PositionRow.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";

@Component({
  name: "ConnectionsTableHtml",
  emits: ["update:modelValue", "recalc", "remove-item", "check-error"],
  components: { NativeInput, PositionRow, AppIcon }
})
export default class ConnectionsTableHtml extends Vue {
  @Prop({ required: true }) modelValue!: ConnectionLine[];
  @Prop({ default: false }) hideRemoveBtn!: boolean;
  @Prop({ default: 100 }) maxPercent!: number;
  @Prop({ default: 0 }) maxAmount!: number;
  @Prop({ default: 0 }) maxPayed!: number;
  @Prop({ default: true }) checkSums!: boolean;
  @Prop({ default: true }) showSums!: boolean;
  @Prop({ default: false }) showNonExistent!: boolean;
  @Prop({ default: false }) showLockStatus!: boolean;

  cur_idx: any = undefined;

  get entityStore() {
    return useEntityStore();
  }

  /** Строка — платёж (не счёт), для таких не показываем статус блокировки */
  isPaymentRow(row: ConnectionLine): boolean {
    if (!row.bill) return false;
    return (
      (row.billRowType === BillRowProps.BillSearchedBase && (row.bill as { Descriptor?: number }).Descriptor === BillSearchedTypeDescriptor.Payment) ||
      (row.billRowType === BillRowProps.BillLinkedBase && (row.bill as { Descriptor?: number }).Descriptor === BillLinkedTypeDescriptor.Payment)
    );
  }

  /** Статус блокировки для строки счёта (только для строк с bill.Id, не для платежей); undefined если не показывать */
  rowLockStatus(row: ConnectionLine): string | undefined {
    if (!this.showLockStatus || !row.bill || this.isPaymentRow(row)) return undefined;
    const billId = (row.bill as { Id?: number }).Id;
    if (billId == null) return undefined;
    const status = this.entityStore.getLockStatus(billId, LockedEntityType.Bill);
    return status && status !== 'locked' ? status : undefined;
  }

  @Emit("recalc") emitRecalc() {
    /* noop */
  }

  get theModel(): ConnectionLine[] {
    if (!this.modelValue) return [];

    // Создаем копию [...], чтобы не мутировать оригинал, и сортируем
    return [...this.modelValue].sort((a, b) => {
      const aHasProject = a.project != null; // проверка на null и undefined
      const bHasProject = b.project != null;

      if (aHasProject && !bHasProject) return -1; // a выше b
      if (!aHasProject && bHasProject) return 1;  // b выше a
      return 0; // сохраняем исходный порядок между ними
    });
  }

  set theModel(v: ConnectionLine[]) {
    this.$emit("update:modelValue", v);
  }

  remove(displayIdx: number) {
    const row = this.theModel[displayIdx];
    if (!row) return;
    this.$emit("remove-item", row);
  }

  getPositionFromNameInstance(pos: StorageItemInMovement) {
    const pos_copy = mapStorageItemInstanceToParameterInstance(
      pos.NameParameters
    );
    const vcode_cell = {
      Type: ParameterInstanceContainerType.SingleInstance,
      Instance: {
        TemplateId: 999,
        Unremovable: true,
        ValueType: V2ParameterValueType.String,
        StringValue: pos.VendorCode,
        NumberValue: null,
        DateValue: null,
        BoolValue: null,
        ValueSet: null,
        SelectedValue: null,
        ValueDisplayType: V2FieldValueDisplayType.Inline,
        Name: this.$t('sku'),
        Id: 0,
        Round: 2,
        RecordAxisType: V2RecordAxisType.Horizontal,
        RecordFields: [],
      } as ParameterInstance,
      ListTitle: null,
    } as ParameterInstanceContainer;

    return [vcode_cell, ...pos_copy];
  }

  windowKeyDown(e: KeyboardEvent) {
    if (e.key == "ArrowLeft") {
      const that = this;
      setTimeout(() => {
        // @ts-ignore
        that.$refs['cancelbtn'+that.cur_idx] && that.$refs['cancelbtn'+that.cur_idx].$el && (that.$refs['cancelbtn'+that.cur_idx].$el.focus());
      }, 100);
    } else if (e.key == "ArrowRight") {
      const that = this;
      setTimeout(() => {
        // @ts-ignore
        that.$refs['okbtn'+that.cur_idx] && that.$refs['okbtn'+that.cur_idx].$el && (that.$refs['okbtn'+that.cur_idx].$el.focus());
      }, 100);
    }
  }

  /** Проверки привязок: по строкам debit <= amount, суммы %/amount/debit. При ошибке эмитит check-error и возвращает false */
  runValidation(): boolean {
    if (!this.showSums || !this.checkSums || !this.modelValue?.length) {
      return true;
    }
    let sum_percent = 0;
    let sum_values = 0;
    let sum_debits = 0;

    for (let conn of this.modelValue) {
      sum_percent += +conn.percent;
      sum_values += +conn.amount;
      sum_debits += +conn.debit;

      if ((conn.debit ?? 0) > (conn.amount ?? 0)) {
        this.$emit('check-error', { type: 0, message: this.$t('debit_cannot_exceed_binding'), conn_id: conn.conn_id });
        return false;
      }
    }

    if (sum_percent < 0 || sum_percent > this.maxPercent) {
      this.$emit('check-error', { type: 1, message: `% ${this.$t('do_not_match')}: ${sum_percent} (${this.$t('maximum')} ${this.maxPercent})`, conn_id: 0 });
      return false;
    }
    if (sum_values < 0 || sum_values > this.maxAmount) {
      this.$emit('check-error', { type: 2, message: this.$t('amounts_do_not_match')+`: ${sum_values} (${this.$t('maximum')} ${this.maxAmount})`, conn_id: 0 });
      return false;
    }
    if (sum_debits > this.maxPayed) {
      this.$emit('check-error', { type: 3, message: this.$t('debit_does_not_match')+`: ${sum_debits} (${this.$t('maximum')} ${this.maxPayed})`, conn_id: 0 });
      return false;
    }
    return true;
  }

  recalcRow(row: ConnectionLine, currentField: string) {
    if (currentField == 'percent') {
      row.amount = +roundTo((row.percent || 0)*(this.maxAmount || !row.maxAmount ? this.maxAmount : row.maxAmount)/100, 6);
    } else if (currentField == 'amount') {
      row.percent = +roundTo(((row.amount || 0)/(this.maxAmount || !row.maxAmount ? this.maxAmount : row.maxAmount))*100, 4);
    }

    if (this.showSums && this.checkSums) {
      if (!this.runValidation()) {
        return;
      }
      // const diff_values = this.maxAmount - sum_values;
      // // add/subtract this value to/from the 1'st connection percent (up to 100) if sum_values == fullBill.CalculatedAmount
      // const diff_percent = this.maxPercent - sum_percent;

      // if (sum_percent === 100 && diff_values !== 0) {
      //   // find first element index on which the addition will be correct
      //   let found_i = -1;
      //   for (let i = 0; i < this.modelValue.length; i++) {
      //     if (this.theModel[i].amount + diff_values >= 0) {
      //       found_i = i;
      //       break;
      //     }
      //   }
      //   if (found_i >= 0) {
      //     this.theModel[found_i].amount += diff_values;
      //   }
      // } else if (sum_values === this.maxAmount && diff_percent !== 0) {
      //   // find first element index on which the addition will be correct
      //   let found_i = -1;
      //   for (let i = 0; i < this.theModel.length; i++) {
      //     if (this.theModel[i].percent + diff_percent >= 0) {
      //       found_i = i;
      //       break;
      //     }
      //   }
      //   if (found_i >= 0) {
      //     this.theModel[found_i].percent += diff_percent;
      //   }
      // }
    }

    // принудить обновление массива
    this.theModel = [...this.theModel];
    this.emitRecalc();

    console.log(currentField)
  }

  get modelMutated() {
    return _.cloneDeep(this.modelValue)
  }

  /* billFromSearched(val: BillSearchedWithConnections): BillInfo {
    let res: BillInfo = {} as BillInfo;

    res.Id = val.Id;
    res.Counterparty = val.Counterparty;
    res.Currency = val.Currency;
    res.Date = val.Date;
    res.IsDraft = val.IsDraft;
    res.Instance1C = val.Instance1C;
    res.Descriptor = val.Descriptor === BillSearchedTypeDescriptor.Payment ? BillLinkedTypeDescriptor.Payment : BillLinkedTypeDescriptor.BillWithManyLinks;
    res.Note = val.Note;
    res.Number = val.Number;
    res.TotalAmount = val.TotalAmount;
    res.Type = val.Type;
    res.HasPayments = val.HasPayments;
    res.PayStatus = val.PayStatus;
    res.PayedPercent = val.PayedPercent;
    res.PayedAmount = val.PayedAmount;
    res.Connections = val.Connections;

    return res
  } */
}
</script>

<style scoped>
.inp {
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  font-size: var(--fs);
  border: 1px solid var(--line);
  border-radius: 6px;
  outline: none;
}

.inp:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 2px #e7f0fe;
}

.pos_cell {
  border-top: 0;
  border-bottom: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.table-remove {
  display: inline-flex;
  line-height: 0;
  vertical-align: middle;
  cursor: pointer;
}

/* В lock.svg цвет обводки прошит в разметке, поэтому проп color иконки его не перебивает,
   а предупреждающий цвет тут смысловой: показывает блокировку другим пользователем. */
.lock-icon :deep(svg path) {
  stroke: #F2C037;
}

</style>
